/**
 * Combined-page Download menu (mirrors downloads/DownloadMenu for the
 * household): the family report PDF, plus "Download All Files" — the v1
 * combined ZIP (family report at the root + a folder per member), plus
 * "Download ETL File" (client 2026-09-09) — the ENTRY application's sheet,
 * the same one its member folder carries in the ZIP. The whole menu is
 * mounted only when `can.download` allows it (gated by the header).
 */
import { ChevronDown, Download, FileDown, Loader2 } from 'lucide-react'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import type { DownloadAvailability } from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import { CloseItem, MENU_PANEL_CLASS, MenuRow } from '../HeaderMenus'
import type { DownloadsApi } from '../downloads/useDownloads'

export function CombinedDownloadMenu({
	downloads,
	api,
}: {
	downloads: DownloadAvailability
	api: DownloadsApi
}) {
	const anyBusy = Boolean(api.busy.family || api.busy.household || api.busy.etl)
	return (
		<DropdownMenu>
			<DropdownMenuTrigger className="group flex h-11 cursor-pointer items-center gap-2 rounded-full border border-[#ECECEA] bg-white px-5 text-sm font-medium text-[#32302F] transition-colors hover:bg-[#F6F7F5]">
				{anyBusy ? (
					<Loader2 className="size-4 animate-spin text-gray-500" />
				) : (
					<Download className="size-4 text-gray-500" />
				)}
				Download
				<ChevronDown className="size-4 text-gray-500 transition-transform duration-200 group-data-[state=open]:rotate-180" />
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				sideOffset={10}
				className={cn(MENU_PANEL_CLASS, 'w-[260px]')}
			>
				<div className="mb-[17px]">
					<CloseItem />
				</div>
				{downloads.familyReport ? (
					<MenuRow
						tone="green"
						icon={<Download />}
						label={
							api.busy.family ? 'Preparing Report...' : 'Download Family Report'
						}
						onSelect={api.downloadFamilyReport}
					/>
				) : null}
				<MenuRow
					tone="rose"
					icon={<FileDown />}
					label={
						api.busy.household ? 'Preparing Files...' : 'Download All Files'
					}
					onSelect={api.downloadHousehold}
				/>
				<MenuRow
					iconSrc="/img/etl-file-download-icon.svg"
					label={api.busy.etl ? 'Preparing Sheet...' : 'Download ETL File'}
					onSelect={api.downloadEtl}
				/>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
